import { useState } from "react";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select } from "@/components/ui/select";
import { Notify, toast } from "@/components/ui/notify";
import { getFriendlyErrorMessage } from "@/lib/api-client";
import { useLines } from "@/features/lines/hooks";
import { TargetForm } from "./TargetForm";
import { useDeleteLineTarget, useLineTargets, useSaveLineTarget } from "./hooks";
import type { LineProductionTargetResponse } from "@/types/production-targets.types";

/**
 * Spec 082 — a meta de PERÍODO de cada linha. A meta diária não é declarada aqui nem em lugar
 * nenhum: ela é derivada desta, dividida pelos dias úteis do calendário, e é premissa — a tela da
 * execução diz isso com todas as letras.
 */
export function TargetsSection({ canEdit }: { canEdit: boolean }) {
  const { data: lines = [] } = useLines();
  const [lineId, setLineId] = useState("");
  const query = useLineTargets(lineId);
  const save = useSaveLineTarget(lineId);
  const remove = useDeleteLineTarget(lineId);

  async function apagar(t: LineProductionTargetResponse) {
    const ok = await Notify.confirm({
      variant: "danger",
      title: "Apagar esta meta?",
      description:
        "Execuções já importadas mantêm o planejado que receberam. A meta deixa de valer para importações novas.",
      confirmLabel: "Apagar meta",
      typeToConfirm: t.periodStart,
    });
    if (!ok) return;
    try {
      await remove.mutateAsync(t.id);
      toast.success("Meta apagada.");
    } catch (err) {
      toast.error("Não foi possível apagar.", { description: getFriendlyErrorMessage(err) });
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Metas de produção</CardTitle>
        <p className="text-xs text-muted-foreground">
          Declaradas por semana ou por mês. A meta de cada dia é derivada dividindo pelos dias úteis
          do calendário — é premissa, não medição.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1.5">
          <label className="text-[13px] font-medium text-text-primary" htmlFor="target-line">
            Linha de produção
          </label>
          <Select id="target-line" value={lineId} onChange={(e) => setLineId(e.target.value)}>
            <option value="">Escolha a linha</option>
            {lines.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </Select>
        </div>

        {!lineId ? null : query.isError ? (
          <Notify.QueryError error={query.error} onRetry={() => void query.refetch()} />
        ) : (
          <>
            {canEdit ? (
              <TargetForm
                isSaving={save.isPending}
                onSubmit={(payload) => {
                  save
                    .mutateAsync(payload)
                    .then(() => toast.success("Meta declarada."))
                    .catch((err: unknown) =>
                      toast.error("Não foi possível declarar.", {
                        description: getFriendlyErrorMessage(err),
                      })
                    );
                }}
              />
            ) : null}

            {(query.data ?? []).length === 0 ? (
              <Notify.Empty
                title="Sem meta declarada nesta linha"
                description="Sem meta, o Desempenho das execuções importadas fica ausente — e ausente não é zero."
              />
            ) : (
              <ul className="divide-y divide-border-default rounded-md border border-border-default">
                {(query.data ?? []).map((t) => (
                  <li key={t.id} className="flex items-center justify-between gap-2 px-3 py-2">
                    <div className="text-[13px]">
                      <span className="font-medium text-text-primary">
                        {t.basis === "WEEKLY" ? "Semana de" : "Mês de"} {t.periodStart}
                      </span>
                      <span className="ml-2 font-mono text-text-secondary">
                        {t.quantity.toLocaleString("pt-BR")}
                      </span>
                    </div>
                    {canEdit ? (
                      <Button
                        variant="outline"
                        size="sm"
                        aria-label={`Apagar meta de ${t.periodStart}`}
                        onClick={() => void apagar(t)}
                      >
                        <Trash2 size={14} />
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
